import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createTenant, deleteTenant, listTenants, listTenantUsers, renameTenant, updateSubscription,
  type SubscriptionInput, type Tenant, type TenantStatus,
} from "@/api/tenants";
import { downloadFile } from "@/api/download";
import { Badge, Button, Card, Input } from "@/components";
import { toast } from "@/state/toast.store";
import { useLang } from "@/i18n/LanguageContext";
import { ManageableUserRow } from "@/features/users/ManageableUserRow";

const PLAN_OPTIONS = ["trial", "starter", "pro", "enterprise", "custom"];
const STATUS_OPTIONS: TenantStatus[] = ["trial", "active", "suspended"];
const STATUS_TONE: Record<TenantStatus, "ok" | "warn" | "err"> = { active: "ok", trial: "warn", suspended: "err" };

export function TenantsPanel() {
  const qc = useQueryClient();
  const { t } = useLang();
  const tenants = useQuery({ queryKey: ["tenants"], queryFn: ({ signal }) => listTenants(signal) });

  const [form, setForm] = useState({ name: "", slug: "", admin_email: "", admin_password: "", admin_full_name: "" });

  const create = useMutation({
    mutationFn: () => createTenant(form),
    onSuccess: () => {
      setForm({ name: "", slug: "", admin_email: "", admin_password: "", admin_full_name: "" });
      toast.ok(t("Tenant aangemaakt.", "Tenant created."));
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  const refetch = () => qc.invalidateQueries({ queryKey: ["tenants"] });

  return (
    <div className="cards" style={{ gridTemplateColumns: "1.2fr 1fr" }}>
      <Card>
        <p className="card-num">{t("TENANTS", "TENANTS")}</p>
        <h3>{t("Klantbedrijven", "Client companies")}</h3>
        {tenants.isLoading ? (
          <p>{t("Laden…", "Loading…")}</p>
        ) : tenants.data && tenants.data.length > 0 ? (
          <ul className="list-rows">
            {tenants.data.map((tn) => <TenantRow key={tn.id} tenant={tn} onChanged={refetch} />)}
          </ul>
        ) : (
          <p className="no-data-message">{t("Nog geen tenants. Maak er hiernaast één aan.", "No tenants yet. Create one on the right.")}</p>
        )}
      </Card>

      <Card>
        <p className="card-num">{t("NIEUW", "NEW")}</p>
        <h3>{t("Tenant toevoegen", "Add a tenant")}</h3>
        <form onSubmit={onSubmit}>
          <Input label={t("Bedrijfsnaam", "Company name")} name="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Slug" name="slug" required placeholder="acme" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          <Input label={t("E-mail org-beheerder", "Org admin email")} name="admin_email" type="email" required value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
          <Input label={t("Wachtwoord org-beheerder", "Org admin password")} name="admin_password" type="password" required minLength={8} value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} />
          <Button type="submit" disabled={create.isPending}>{create.isPending ? t("Bezig…", "Working…") : t("Tenant aanmaken", "Create tenant")}</Button>
        </form>
      </Card>
    </div>
  );
}

function TenantRow({ tenant, onChanged }: { tenant: Tenant; onChanged: () => void }) {
  const qc = useQueryClient();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(tenant.name);
  const [sub, setSub] = useState<SubscriptionInput>({
    plan: tenant.plan, seat_limit: tenant.seat_limit, status: tenant.status,
    trial_ends_at: tenant.trial_ends_at, notes: tenant.notes,
  });

  const users = useQuery({
    queryKey: ["tenant-users", tenant.id],
    queryFn: ({ signal }) => listTenantUsers(tenant.id, signal),
    enabled: open,
  });

  const rename = useMutation({ mutationFn: () => renameTenant(tenant.id, name), onSuccess: () => { toast.ok(t("Opgeslagen.", "Saved.")); onChanged(); } });
  const saveSub = useMutation({ mutationFn: () => updateSubscription(tenant.id, sub), onSuccess: () => { toast.ok(t("Abonnement bijgewerkt.", "Subscription updated.")); onChanged(); } });
  const del = useMutation({ mutationFn: () => deleteTenant(tenant.id), onSuccess: () => { toast.ok(t("Tenant verwijderd.", "Tenant deleted.")); onChanged(); } });

  function onDelete() {
    const msg = t(
      `Tenant "${tenant.name}" en ALLE bijbehorende data (gebruikers, content, trajecten, audit, bestanden) verwijderen? Dit kan niet ongedaan worden.`,
      `Delete tenant "${tenant.name}" and ALL its data (users, content, journeys, audit, files)? This cannot be undone.`,
    );
    if (window.confirm(msg)) del.mutate();
  }
  async function exportData() {
    try {
      await downloadFile(`/api/tenants/${tenant.id}/export`, `onboardforge-export-${tenant.slug}.json`);
    } catch (e) {
      toast.err(e instanceof Error ? e.message : t("Export mislukt", "Export failed"));
    }
  }
  const refetchUsers = () => qc.invalidateQueries({ queryKey: ["tenant-users", tenant.id] });
  const seats = tenant.seat_limit == null ? "∞" : tenant.seat_limit;

  return (
    <li style={{ flexDirection: "column", alignItems: "stretch", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <strong>{tenant.name}</strong>
          <Badge>{tenant.slug}</Badge>
          <Badge tone={STATUS_TONE[tenant.status]}>{tenant.status}</Badge>
          <Badge tone="neutral">{tenant.plan} · {tenant.seats_used ?? 0}/{seats}</Badge>
        </span>
        <button type="button" className="btn btn-outline" style={{ padding: "6px 12px", fontSize: 13 }} onClick={() => setOpen((v) => !v)}>
          {open ? t("Sluiten", "Close") : t("Beheer", "Manage")}
        </button>
      </div>

      {open && (
        <div style={{ background: "var(--bg)", borderRadius: 8, padding: 16 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
            <div className="field" style={{ flex: 1, marginBottom: 0, minWidth: 160 }}>
              <label>{t("Bedrijfsnaam", "Company name")}</label>
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <Button variant="outline" onClick={() => rename.mutate()} disabled={rename.isPending}>{t("Hernoemen", "Rename")}</Button>
            <Button variant="outline" onClick={exportData}>{t("Data-export", "Data export")}</Button>
            <Button variant="outline" onClick={onDelete} disabled={del.isPending}>{t("Verwijderen", "Delete")}</Button>
          </div>

          <p className="card-num" style={{ marginBottom: 8 }}>{t("ABONNEMENT", "SUBSCRIPTION")}</p>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>{t("Plan", "Plan")}</label>
              <select value={sub.plan} onChange={(e) => setSub({ ...sub, plan: e.target.value })}>
                {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="field" style={{ marginBottom: 0, maxWidth: 120 }}>
              <label>{t("Seats", "Seats")}</label>
              <input type="number" min={0} value={sub.seat_limit ?? ""} placeholder="∞"
                onChange={(e) => setSub({ ...sub, seat_limit: e.target.value === "" ? null : Number(e.target.value) })} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>{t("Status", "Status")}</label>
              <select value={sub.status} onChange={(e) => setSub({ ...sub, status: e.target.value as TenantStatus })}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Button onClick={() => saveSub.mutate()} disabled={saveSub.isPending}>{t("Opslaan", "Save")}</Button>
          </div>

          <p className="card-num" style={{ marginBottom: 8 }}>{t("GEBRUIKERS", "USERS")}</p>
          {users.isLoading ? (
            <p style={{ color: "var(--grey-text)" }}>{t("Laden…", "Loading…")}</p>
          ) : users.data && users.data.length > 0 ? (
            <ul className="list-rows">
              {users.data.map((u) => (
                <ManageableUserRow key={u.id} user={u} actorRole="platform_admin" onChanged={refetchUsers} />
              ))}
            </ul>
          ) : (
            <p className="no-data-message">{t("Geen gebruikers.", "No users.")}</p>
          )}
        </div>
      )}
    </li>
  );
}
